import FlexSearch from 'flexsearch';
import { useCallback, useMemo, useRef } from 'react';
import { useLineageFlowContext } from '../../context/LineageFlowProvider/LineageFlowContext';

export const useEntityNodeChildren = (
	id: string,
	debouncedSearchTerm: string
) => {
	const {
		parentChildMap,
		highlightedParentChildMap,
		impactChildID,
		fetchedChildrenIDs,
		expandedNodeIDs,
		onUpdatePositionsAfterNodeChildrenToggled,
	} = useLineageFlowContext();

	const fetched = fetchedChildrenIDs.has(id);
	const expanded = expandedNodeIDs.has(id);
	const children = parentChildMap.get(id);
	const highlightedChildren = highlightedParentChildMap.get(id);

	const hasChildren = Boolean(children?.length);
	const hasHighlightedChildren = Boolean(highlightedChildren?.length);

	// Create stable dependency keys based on child IDs instead of array references
	// This prevents unnecessary index rebuilds when arrays are recreated with same content
	const childrenIds =
		children
			?.map((c) => c.id)
			.sort()
			.join(',') || '';
	const highlightedChildrenIds =
		highlightedChildren
			?.map((c) => c.id)
			.sort()
			.join(',') || '';

	// Use ref to track previous IDs and only rebuild index when IDs actually change
	const prevIdsRef = useRef<string>('');
	const indexRef = useRef<FlexSearch.Index | null>(null);

	const index = useMemo(() => {
		const currentIds = `${childrenIds}|${highlightedChildrenIds}`;
		if (
			indexRef.current &&
			prevIdsRef.current === currentIds &&
			prevIdsRef.current !== ''
		) {
			// IDs haven't changed, return cached index
			return indexRef.current;
		}

		// IDs changed, rebuild index
		const idx = new FlexSearch.Index({
			preset: 'memory',
			tokenize: 'forward',
		});
		children?.forEach((child) => {
			idx.add(child.id, child.title);
		});
		highlightedChildren?.forEach((child) => {
			idx.add(child.id, child.title);
		});

		// Cache the index and IDs
		indexRef.current = idx;
		prevIdsRef.current = currentIds;

		return idx;
	}, [childrenIds, highlightedChildrenIds, children, highlightedChildren]);

	const filteredChildren = useMemo(() => {
		if (!debouncedSearchTerm) {
			return highlightedChildren || children;
		}

		const searchResults = index.search(debouncedSearchTerm) as string[];

		if (highlightedChildren?.length) {
			return highlightedChildren.filter((child) =>
				searchResults.includes(child.id)
			);
		}

		return children?.filter((child) => searchResults.includes(child.id));
	}, [children, highlightedChildren, debouncedSearchTerm, index]);

	const hasFilteredChildren = Boolean(filteredChildren?.length);

	const showSkeletons = expanded && !hasFilteredChildren;
	const showChildren =
		((fetched && hasChildren) || hasHighlightedChildren) && hasFilteredChildren;

	const handleToggleExpand = useCallback(() => {
		onUpdatePositionsAfterNodeChildrenToggled({
			toggledNodeID: id,
			expanded: !expanded,
			fetched,
		});
	}, [onUpdatePositionsAfterNodeChildrenToggled, id, expanded, fetched]);

	return {
		expanded,
		highlightedChildren,
		hasHighlightedChildren,
		filteredChildren,
		impactChildID,
		showSkeletons,
		showChildren,
		handleToggleExpand,
	};
};
